/**
 * Line lowering: Line record + tracks + notes -> IrLine (W1-04 core-ir; rpe-format.md F§5, F§9, F§10, F§12,
 * DESIGN.md §6.3, §6.10, §6.12, §6.13).
 *
 *  - Name, Texture ("line.png" or the asset's package path via `LowerOptions.assetPath`) and textureKind;
 *  - father: export index of the parent or -1; rotateWithFather; isCover (1 / 0, or the preserved raw integer while
 *    it means the same); zOrder; attachUI; Group (index in judgeLineGroup); bpmfactor; anchor;
 *  - isGif (the texture is a GIF Phira decodes) and numOfNotes (real notes) are re-derived on every export;
 *  - eventLayers: one slot per layer index up to the highest layer with tracks or the imported slot count; slots
 *    without tracks keep their imported style (`null` or `{}`), layer passthrough and explicit-null list keys;
 *  - extended: the typed lists; `gifEvents: []` for a GIF that plays automatically (the only empty list ever
 *    written, asserted to be on a GIF texture); lists Phira ignores for rendering on this line kind written back in
 *    place (`ignoredLists`: text / paint on a textured line, gif on `line.png`); explicit-null keys; passthrough;
 *    present as `{}`, `null` or absent as imported when it holds nothing;
 *  - notes (lowerNotes.ts) and the notes key (absent only when imported without one and still empty);
 *  - note controls, skewControl, unknown line keys;
 *  - neutral padding for editor lines in the RPE-compatible profile (pad.ts).
 * GIF data is asserted to be on a GIF texture: Phira decodes any non-line.png texture with its GIF decoder as soon
 * as gifEvents exists, and a PNG / JPG then fails the whole chart.
 */

import { EXT_PROPS, LAYERED_PROPS } from '../model/props';
import type { ExtProp, LayeredProp, Line, LineId, Track } from '../model/types';
import { AssertionError } from '../util/assert';
import type { Json, RGB } from '../util/types';
import { controlOut, eventJson, listKeyOf, type MutableIrLine } from './irShape';
import { BUILTIN_TEXTURE, textureKindOf, type LowerCtx } from './lowerCtx';
import { countRealNotes, lowerNotes } from './lowerNotes';
import { lowerLineTrack, type LineEventValue } from './lowerTrack';
import { padEditorLine } from './pad';
import type { IrEventList, IrExtended, IrLayer, IrLine } from './types';

type MutableLayer = { -readonly [K in keyof IrLayer]: IrLayer[K] };
type MutableExtended = { -readonly [K in keyof IrExtended]: IrExtended[K] };

function lineOf(ctx: LowerCtx, id: LineId): Line {
  const line = ctx.doc.lines.get(id);
  if (line === undefined) throw new AssertionError(`lower: line ${String(id)} does not exist`);
  return line;
}

/** Tracks of a line per property, in layer order. */
function lineTracks(ctx: LowerCtx, line: LineId): Map<string, Track[]> {
  const out = new Map<string, Track[]>();
  for (const [prop, ids] of ctx.idx.tracksOfLine(line)) {
    const tracks: Track[] = [];
    for (const id of ids) {
      const t = ctx.doc.tracks.get(id);
      if (t !== undefined) tracks.push(t);
    }
    out.set(prop, tracks);
  }
  return out;
}

function buildLayers(ctx: LowerCtx, line: Line, tracks: Map<string, Track[]>): (IrLayer | null)[] {
  const byLayer = new Map<number, Map<LayeredProp, IrEventList<number>>>();
  let maxLayer = -1;
  for (const prop of LAYERED_PROPS) {
    for (const t of tracks.get(prop) ?? []) {
      if (t.owner.kind !== 'line') continue;
      const events = lowerLineTrack(ctx, t);
      if (events === null) continue;
      const layer = t.owner.layer;
      let m = byLayer.get(layer);
      if (m === undefined) {
        m = new Map();
        byLayer.set(layer, m);
      }
      m.set(prop, events as unknown as IrEventList<number>);
      if (layer > maxLayer) maxLayer = layer;
    }
  }
  const rpe = line.rpe;
  const slotCount = Math.max(maxLayer + 1, rpe.layerSlots.length);
  const out: (IrLayer | null)[] = [];
  for (let i = 0; i < slotCount; i++) {
    const lists = byLayer.get(i);
    const hasLists = lists !== undefined && lists.size > 0;
    if (!hasLists && rpe.layerSlots[i] !== 'object') {
      out.push(null);
      continue;
    }
    const layer: MutableLayer = { nullKeys: [], extra: rpe.layerExtra[i] ?? [] };
    const present = new Set<string>();
    if (lists !== undefined) {
      for (const [prop, events] of lists) {
        const key = listKeyOf(prop) as `${LayeredProp}Events`;
        layer[key] = events;
        present.add(key);
      }
    }
    // Explicit-null list keys are written only while the list stays absent.
    layer.nullKeys = (rpe.nullKeys.layers[i] ?? []).filter((k) => !present.has(k));
    out.push(layer);
  }
  return out;
}

/** Extended object; `content` is false when it holds no list, explicit-null key or passthrough. */
function buildExtended(
  ctx: LowerCtx,
  line: Line,
  textureKind: IrLine['textureKind'],
  tracks: Map<string, Track[]>,
): { readonly ext: MutableExtended; readonly content: boolean } {
  const ext: MutableExtended = { ignoredLists: [], nullKeys: [], extra: line.rpe.extendedExtra };
  const ignored: Array<readonly [string, Json]> = [];
  let content = false;
  for (const prop of EXT_PROPS) {
    const t = tracks.get(prop)?.[0];
    if (t === undefined) continue;
    const events = lowerLineTrack(ctx, t);
    if (events === null) continue;
    const key = listKeyOf(prop);
    content = true;
    if ((prop === 'text' || prop === 'paint') && textureKind !== 'builtin') {
      // Phira ignores text / paint on a textured line: kept verbatim, still scanned like Phira scans it.
      ignored.push([key, events.map((e) => eventJson(e))]);
      continue;
    }
    if (prop === 'gif' && textureKind !== 'gif') {
      throw new AssertionError(
        `lower: line ${String(line.id)} has GIF progress keys but its texture is not a GIF (Phira would fail to load the chart)`,
      );
    }
    assignExtList(ext, prop, events);
  }
  if (line.gifAutoLoop) {
    if (textureKind !== 'gif') {
      throw new AssertionError(
        `lower: line ${String(line.id)} plays a GIF automatically but its texture is not a GIF (Phira would fail to load the chart)`,
      );
    }
    if (ext.gifEvents === undefined) {
      ext.gifEvents = [];
      content = true;
    }
  }
  for (const item of line.rpe.ignored) {
    if (item.writeBack !== 'extended') continue;
    const allowed =
      (item.key === 'gifEvents' && textureKind === 'builtin') ||
      ((item.key === 'textEvents' || item.key === 'paintEvents') && textureKind !== 'builtin');
    if (!allowed) continue;
    if (ignored.some(([k]) => k === item.key)) continue;
    if (ext[item.key as keyof MutableExtended] !== undefined) continue;
    ignored.push([item.key, item.value]);
    content = true;
  }
  ext.ignoredLists = ignored;
  const present = new Set<string>(ignored.map(([k]) => k));
  for (const prop of EXT_PROPS)
    if (ext[listKeyOf(prop) as `${ExtProp}Events`] !== undefined) present.add(listKeyOf(prop));
  // Explicit-null list keys are written only while the list stays absent.
  ext.nullKeys = line.rpe.nullKeys.extended.filter((k) => !present.has(k));
  return { ext, content: content || ext.nullKeys.length > 0 || ext.extra.length > 0 };
}

function assignExtList(ext: MutableExtended, prop: ExtProp, events: IrEventList<LineEventValue>): void {
  switch (prop) {
    case 'scaleX':
      ext.scaleXEvents = events as unknown as IrEventList<number>;
      break;
    case 'scaleY':
      ext.scaleYEvents = events as unknown as IrEventList<number>;
      break;
    case 'color':
      ext.colorEvents = events as unknown as IrEventList<RGB>;
      break;
    case 'text':
      ext.textEvents = events as unknown as IrEventList<string>;
      break;
    case 'incline':
      ext.inclineEvents = events as unknown as IrEventList<number>;
      break;
    case 'paint':
      ext.paintEvents = events as unknown as IrEventList<number>;
      break;
    case 'gif':
      ext.gifEvents = events as unknown as IrEventList<number>;
      break;
  }
}

/** The IrLine of one line (padding included when the profile and origin call for it). */
export function lowerLineIr(ctx: LowerCtx, id: LineId): IrLine {
  const { doc, idx, opts } = ctx;
  const line = lineOf(ctx, id);
  const textureKind = textureKindOf(doc, line);
  const tracks = lineTracks(ctx, id);
  const rpe = line.rpe;
  const notes = lowerNotes(ctx, id);
  const isCoverRaw = rpe.isCoverRaw;
  const ir: MutableIrLine = {
    srcLine: id,
    Name: line.name,
    Texture: line.texture === null ? BUILTIN_TEXTURE : ctx.assetPath(line.texture),
    textureKind,
    father: line.parent === null ? -1 : idx.lineIndex(line.parent),
    rotateWithFather: line.rotateWithParent,
    isCover: line.cover ? 1 : isCoverRaw !== null && isCoverRaw !== 1 ? isCoverRaw : 0,
    zOrder: line.zOrder,
    attachUI: line.attachUI,
    Group: ctx.groupIndex(line.group),
    bpmfactor: rpe.bpmfactor,
    anchor: rpe.anchor,
    isGif: textureKind === 'gif',
    numOfNotes: countRealNotes(ctx, id),
    eventLayers: buildLayers(ctx, line, tracks),
    extended: 'absent',
    notes,
    notesKey: rpe.notesKey === 'absent' && notes.length === 0 ? 'absent' : 'present',
    extra: rpe.extra,
  };
  const { ext, content } = buildExtended(ctx, line, textureKind, tracks);
  if (content) ir.extended = ext;
  else if (rpe.extended === 'null') ir.extended = null;
  // An empty `{}` as imported (or RPE-style on editor lines), omitted for editor lines in the minimal profile.
  else if (rpe.extended === 'object' && !(line.origin === 'editor' && opts.profile === 'minimal')) ir.extended = ext;
  const c = line.controls;
  if (c.pos !== null) ir.posControl = controlOut(c.pos);
  if (c.alpha !== null) ir.alphaControl = controlOut(c.alpha);
  if (c.size !== null) ir.sizeControl = controlOut(c.size);
  if (c.y !== null) ir.yControl = controlOut(c.y);
  if (rpe.skewControl !== null) ir.skewControl = rpe.skewControl;
  if (line.origin === 'editor' && opts.profile === 'rpe-compatible') {
    const hasSpeed = (tracks.get('speed') ?? []).length > 0;
    if (padEditorLine(ir, hasSpeed)) ctx.acc.paddedLines.push(id);
  }
  return ir;
}
